import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Flame, TrendingUp, Search, Play, Star } from 'lucide-react';
import { gamesApi, Game } from '../services/api';
import SEO from '../components/SEO';

const SlotPopuler: React.FC = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadGames = async () => {
      try {
        const result = await gamesApi.list({ category: 'slots', limit: 50 });
        if (result.success && result.data?.games) {
          setGames(result.data.games);
        }
      } catch (error) {
        console.error('Oyunlar yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    loadGames();
  }, []);

  const filteredGames = games.filter(game =>
    game.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    game.provider.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <SEO
        title="Popüler Oyunlar | En Çok Oynananlar"
        description="En popüler ve en çok oynanan slot oyunlarını keşfedin."
        url="/slotlar/populer"
      />
      <div className="min-h-screen pt-24 pb-12 relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
              <Flame className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Popüler Oyunlar
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              En çok oynanan slot oyunları
            </p>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="max-w-md mx-auto mb-8"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Oyun veya sağlayıcı ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-dark-800 border border-dark-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 transition-colors"
              />
            </div>
          </motion.div>

          {/* Loading State */}
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-dark-800 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : filteredGames.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 border border-dark-700 text-center"
            >
              <TrendingUp className="w-16 h-16 text-primary-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-4">
                {searchTerm ? 'Oyun Bulunamadı' : 'Yakında'}
              </h2>
              <p className="text-gray-400">
                {searchTerm
                  ? 'Arama kriterlerinize uygun oyun bulunamadı.'
                  : 'Popüler oyunlar yakında burada olacak. Takipte kalın!'}
              </p>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4"
            >
              {filteredGames.map((game, index) => (
                <motion.div
                  key={game.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="group relative aspect-[3/4] rounded-xl overflow-hidden bg-dark-800 border border-dark-700 hover:border-orange-500/50 transition-all duration-300 cursor-pointer"
                >
                  {/* Popular Badge */}
                  <div className="absolute top-2 right-2 z-10 px-2 py-1 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg text-xs font-bold text-white">
                    #{index + 1}
                  </div>

                  {/* Game Image */}
                  <img
                    src={game.thumbnailUrl || '/placeholder-game.png'}
                    alt={game.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />

                  {/* Play Button - Hover */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-14 h-14 rounded-full bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/50 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                      <Play className="w-6 h-6 text-white ml-1" />
                    </div>
                  </div>

                  {/* Game Info */}
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <h3 className="text-sm font-semibold text-white truncate mb-1">
                      {game.name}
                    </h3>
                    <p className="text-xs text-gray-400 truncate flex items-center">
                      <Star className="w-3 h-3 text-yellow-500 mr-1" />
                      {game.provider}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Game Count */}
          {!loading && filteredGames.length > 0 && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center text-gray-500 mt-8"
            >
              Toplam {filteredGames.length} oyun
            </motion.p>
          )}
        </div>
      </div>
    </>
  );
};

export default SlotPopuler;
